// Why a label blocks the import (REQ-06), why a sample code is malformed
// (C52) or why a row is ignored (REQ-07): i18n keys, icons and the pure rules
// the summaries render.

const I18N = 'analysesData.importValues';
const IGNORED_REASONS_I18N = `${I18N}.ignoredRows.reasons`;

export const UNKNOWN = 'unknown';
export const MALFORMED_SAMPLE_CODE = 'malformedSampleCode';

export function sortBySourceRow(items) {
  return [...items].sort((a, b) => a.sourceRow - b.sourceRow);
}

// Counts of the keys present, in the order given.
function countInOrder(keys, order) {
  return order
    .map((key) => ({ key, count: keys.filter((item) => item === key).length }))
    .filter(({ count }) => count > 0);
}

// A family of causes read from `keyField` of each item: icon, title and fix
// per cause, a generic text for a cause it does not know. The order of
// `icons` is the order of the causes in a summary.
function issueCatalog({ i18nPrefix, keyField, icons }) {
  const keys = Object.keys(icons);
  const byKey = (candidate) => {
    const key = keys.includes(candidate) ? candidate : UNKNOWN;
    return {
      key,
      icon: icons[key],
      titleKey: `${i18nPrefix}.${key}.title`,
      fixKey: `${i18nPrefix}.${key}.fix`,
    };
  };
  const issueOf = (item) => byKey(item[keyField]);
  return {
    issueOf,
    count: (items) =>
      countInOrder(
        items.map((item) => issueOf(item).key),
        keys
      ).map(({ key, count }) => ({ ...byKey(key), count })),
    // One line per item, tab separated (row, label, issue, fix): pastes into
    // Excel or a ticket as is. `t` translates an i18n key.
    toTsv: (items, t) =>
      sortBySourceRow(items)
        .map((item) => {
          const { titleKey, fixKey } = issueOf(item);
          return [item.sourceRow, item.label, t(titleKey), t(fixKey)].join(
            '\t'
          );
        })
        .join('\n'),
  };
}

// Labels that block the import, by `reason`.
const LABEL_ISSUES = issueCatalog({
  i18nPrefix: `${I18N}.labelIssues`,
  keyField: 'reason',
  icons: {
    notFound: 'el-icon-search',
    duplicate: 'el-icon-copy-document',
    [UNKNOWN]: 'el-icon-warning-outline',
  },
});

// Ignored rows with a malformed sample code, by `detail`.
const MALFORMED_CODES = issueCatalog({
  i18nPrefix: `${I18N}.malformedCodes`,
  keyField: 'detail',
  icons: {
    missingLocationLetter: 'el-icon-location-outline',
    unexpectedFormat: 'el-icon-edit-outline',
    [UNKNOWN]: 'el-icon-warning-outline',
  },
});

export const labelIssue = LABEL_ISSUES.issueOf;
export const countLabelIssues = LABEL_ISSUES.count;
export const labelIssuesToTsv = LABEL_ISSUES.toTsv;

export const malformedCodeIssue = MALFORMED_CODES.issueOf;
export const countMalformedCodes = MALFORMED_CODES.count;
export const malformedCodesToTsv = MALFORMED_CODES.toTsv;

// One summary line per counted cause: title, count and how to fix it.
export function issueLines(counts, t) {
  return counts.map(({ key, icon, titleKey, fixKey, count }) => ({
    key,
    icon,
    text: t(`${I18N}.issueLine`, { title: t(titleKey), count, fix: t(fixKey) }),
  }));
}

// Malformed sample codes first: the only ignored rows that usually ask for a
// fix (C53).
const IGNORED_ROW_REASONS = Object.freeze([
  MALFORMED_SAMPLE_CODE,
  'emptyLabel',
  'notSampleCode',
  UNKNOWN,
]);

export function isMalformedSampleCode({ reason }) {
  return reason === MALFORMED_SAMPLE_CODE;
}

export function ignoredRowReasonKey(reason) {
  const key = IGNORED_ROW_REASONS.includes(reason) ? reason : UNKNOWN;
  return `${IGNORED_REASONS_I18N}.${key}`;
}

export function countIgnoredRowReasons(rows) {
  return countInOrder(
    rows.map(({ reason }) => ignoredRowReasonKey(reason)),
    IGNORED_ROW_REASONS.map(ignoredRowReasonKey)
  ).map(({ key, count }) => ({ reasonKey: key, count }));
}
